import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getAwStats, getAwZmenyCen, listAwDily, listAwKola,
  schvalitAwDil, schvalitAwKolo, skrytAwDil, skrytAwKolo,
  type AwDil, type AwKolo, type AwStav,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";

/**
 * ANGEL'S WHEELS — kola Kite Elite / G2 ProSeries a díly kol.
 *
 * Rozhodnutí Víta 3. 9. 2026: jen KTM od 2017 · konfigurátor NEstavět →
 * 4 KARTY KOL (SX/EXC × Kite/G2) s variantami kolo × ráfek (ceny z jejich
 * konfigurátoru), barvy jako volby na kartě · náhradní díly jako běžné karty.
 *
 * 💰 Cena = cena dodavatele 1 : 1 (Vít 8. 9. 2026: „bude malá marže, nevadí").
 *    Dealerská sleva je 10 % plošně ⇒ Angel's je MIMO zákaznické slevy.
 * 🔑 Nahoře 4 karty kol (schvalují se jako celek), dole díly po policích.
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

function StavBadge({ stav }: { stav: AwStav }): JSX.Element | null {
  if (stav === "na-eshopu") return <Badge tone="green">Na eshopu</Badge>;
  if (stav === "ceka") return <Badge tone="blue">Čeká na robota</Badge>;
  if (stav === "skryto") return <Badge tone="neutral">Skryto</Badge>;
  if (stav === "chyba") return <Badge tone="red">Chyba</Badge>;
  return null;
}

function useAkce(onZmena: () => void): [boolean, string | null, (fn: () => Promise<void>) => Promise<void>] {
  const [pracuje, setPracuje] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const akce = async (fn: () => Promise<void>): Promise<void> => {
    setPracuje(true);
    setChyba(null);
    try {
      await fn();
      onZmena();
    } catch (e) {
      setChyba(e instanceof Error ? e.message : "nepovedlo se");
    } finally {
      setPracuje(false);
    }
  };
  return [pracuje, chyba, akce];
}

function KartaKola({ k, onZmena }: { k: AwKolo; onZmena: () => void }): JSX.Element {
  const [pracuje, chyba, akce] = useAkce(onZmena);
  const dost = k.body.filter((b) => b.dostupne);
  const od = dost.length ? Math.min(...dost.map((b) => Number(b.naseCena ?? Infinity))) : null;
  const bodu = k.body.filter((b) => b.dostupne).length;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 hover:border-ktm-orange hover:shadow-md transition-all p-3 flex flex-col">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Link to={`/angelswheels/kola/${k.key}`} className="font-medium text-zinc-900 hover:text-ktm-orange leading-snug">
            {k.nazev}
          </Link>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            {k.naboj === "kite" ? "náboje Kite Elite" : "náboje G2 ProSeries"} · {[k.predni, k.zadni].filter(Boolean).join(" + ")} · {k.model}
          </div>
        </div>
        <StavBadge stav={k.stav} />
      </div>
      <div className="mt-2 text-[12px] space-y-0.5">
        <div className="flex justify-between text-zinc-500"><span>cena od</span><span className="tabular-nums font-semibold text-zinc-900">{kc(od)}</span></div>
        <div className="flex justify-between text-zinc-500"><span>cenových bodů</span><span className="tabular-nums">{bodu} / {k.body.length}</span></div>
        <div className="flex justify-between text-zinc-500">
          <span>barvy</span>
          <span>{k.barvy.hub?.length ?? 0} náby · {k.barvy.nipple?.length ?? 0} niplů · {k.barvy.spokes?.length ?? 0} paprsků</span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-1">
        {k.stav === "na-eshopu" || k.stav === "ceka" ? (
          <Button size="sm" variant="ghost" disabled={pracuje} onClick={() => void akce(() => skrytAwKolo(k.key))}>Skrýt z eshopu</Button>
        ) : (
          <Button size="sm" disabled={pracuje || bodu === 0} onClick={() => void akce(() => schvalitAwKolo(k.key))}>Na eshop</Button>
        )}
        <Link to={`/angelswheels/kola/${k.key}`} className="ml-auto text-[11px] text-zinc-400 hover:text-ktm-orange">detail a ceny</Link>
      </div>
      {k.foundedError && <div className="text-[11px] text-red-600 mt-1">Robot hlásí: {k.foundedError}</div>}
      {chyba && <div className="text-[11px] text-red-600 mt-1">{chyba}</div>}
    </div>
  );
}

function KartaDilu({ p, onZmena }: { p: AwDil; onZmena: () => void }): JSX.Element {
  const [pracuje, chyba, akce] = useAkce(onZmena);
  return (
    <div className="group bg-white rounded-xl border border-zinc-200 overflow-hidden transition-all hover:border-ktm-orange hover:shadow-md flex flex-col">
      <Link to={`/angelswheels/dily/${encodeURIComponent(p.ref)}`} className="block">
        <div className="aspect-[4/3] bg-zinc-50 relative overflow-hidden">
          {p.fotka ? (
            <img src={p.fotka} alt={p.nazev} loading="lazy" className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform" />
          ) : (
            <div className="w-full h-full grid place-items-center text-zinc-300 text-xs">bez fotky</div>
          )}
          <div className="absolute bottom-1.5 left-1.5 flex gap-1 flex-wrap">
            {p.ktmFit && <Badge tone="amber">KTM</Badge>}
            {p.univerzal && <Badge tone="neutral">univerzál</Badge>}
            {p.skladVlasim > 0 && <Badge tone="green">Vlašim {p.skladVlasim} ks</Badge>}
            {p.skladDodavatele === "neni" && <Badge tone="red">dodavatel nemá</Badge>}
            <StavBadge stav={p.stav} />
          </div>
          {p.varianty.length > 1 && (
            <div className="absolute top-1.5 right-1.5 rounded bg-white/90 border border-zinc-200 px-1 py-0.5 text-[10px] text-zinc-600">
              {p.varianty.length} var.
            </div>
          )}
        </div>
      </Link>
      <div className="p-2.5 flex-1 flex flex-col">
        <div className="text-[11px] text-zinc-400 truncate">{p.policeNazev} · <span className="font-mono">{p.ref}</span></div>
        <Link to={`/angelswheels/dily/${encodeURIComponent(p.ref)}`} className="mt-0.5 text-[13px] font-medium text-zinc-900 line-clamp-2 min-h-[2.2rem] leading-snug hover:text-ktm-orange">
          {p.nazev}
        </Link>
        <div className="mt-1.5 text-[12px] space-y-0.5">
          <div className="flex justify-between text-zinc-500"><span>dodavatel</span><span className="tabular-nums">{kc(p.cenaDodavatele)}</span></div>
          <div className="flex justify-between font-semibold text-zinc-900">
            <span>naše</span>
            <span className="tabular-nums">{kc(p.naseCena)}{p.cenaRucne && <span className="ml-1 text-amber-600" title="ruční cena">✎</span>}</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-1">
          {p.stav === "na-eshopu" || p.stav === "ceka" ? (
            <Button size="sm" variant="ghost" disabled={pracuje} onClick={() => void akce(() => skrytAwDil(p.ref))}>Skrýt z eshopu</Button>
          ) : (
            <Button size="sm" disabled={pracuje} onClick={() => void akce(() => schvalitAwDil(p.ref))}>Na eshop</Button>
          )}
          <Link to={`/angelswheels/dily/${encodeURIComponent(p.ref)}`} className="ml-auto text-[11px] text-zinc-400 hover:text-ktm-orange">detail</Link>
        </div>
        {chyba && <div className="text-[11px] text-red-600 mt-1">{chyba}</div>}
      </div>
    </div>
  );
}

function ZmenyCen(): JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof getAwZmenyCen>> | null>(null);
  const [otevreno, setOtevreno] = useState(false);
  useEffect(() => { void getAwZmenyCen(30).then(setData).catch(() => setData(null)); }, []);
  if (!data || data.zmeny.length === 0) return null;
  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <button type="button" onClick={() => setOtevreno((o) => !o)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
        <span className="font-medium text-sm">Změny cen u dodavatele</span>
        <Badge tone="blue">{data.zmeny.length} za {data.dnu} dní</Badge>
        <span className="ml-auto text-zinc-400 text-sm">{otevreno ? "▲" : "▼"}</span>
      </button>
      {otevreno && (
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">Kdy</th><th className="py-2 pr-3 text-left">Položka</th>
                <th className="py-2 pr-3 text-right">Nová cena</th><th className="py-2 text-right">Rozdíl</th>
              </tr>
            </thead>
            <tbody>
              {data.zmeny.map((z, i) => {
                const r = z.rozdil === null ? 0 : Number(z.rozdil);
                return (
                  <tr key={`${z.ref}-${i}`} className="border-t border-zinc-100">
                    <td className="py-1.5 pr-3 text-zinc-500 whitespace-nowrap">{new Date(z.kdy).toLocaleDateString("cs-CZ")}</td>
                    <td className="py-1.5 pr-3"><Link to={z.odkaz} className="hover:text-ktm-orange">{z.nazev}</Link> <span className="text-zinc-400 font-mono text-xs">{z.ref}</span></td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{kc(z.cena)}</td>
                    <td className={`py-1.5 text-right tabular-nums ${r < 0 ? "text-green-700" : r > 0 ? "text-red-700" : "text-zinc-400"}`}>
                      {r === 0 ? "—" : `${r > 0 ? "+" : ""}${Math.round(r).toLocaleString("cs-CZ")} Kč`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function AngelswheelsPage(): JSX.Element {
  const [kola, setKola] = useState<AwKolo[] | null>(null);
  const [dily, setDily] = useState<AwDil[] | null>(null);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getAwStats>> | null>(null);
  const [police, setPolice] = useState<string | null>(null);
  const [jenVedeme, setJenVedeme] = useState(false);
  const [hledat, setHledat] = useState("");
  const [loading, setLoading] = useState(false);

  const nactiKola = useCallback(async () => {
    const r = await listAwKola();
    setKola(r.kola);
  }, []);
  const nactiDily = useCallback(async () => {
    setLoading(true);
    try {
      const dotaz: Parameters<typeof listAwDily>[0] = { jenVedeme };
      if (police) dotaz.police = police;
      if (hledat.trim()) dotaz.hledat = hledat.trim();
      const r = await listAwDily(dotaz);
      setDily(r.dily);
    } finally {
      setLoading(false);
    }
  }, [police, jenVedeme, hledat]);

  useEffect(() => { void nactiKola(); }, [nactiKola]);
  useEffect(() => { void nactiDily(); }, [nactiDily]);
  useEffect(() => { void getAwStats().then(setStats).catch(() => setStats(null)); }, []);

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Angel&apos;s Wheels — kola Kite Elite / G2 ProSeries</h1>
        <p className="text-sm text-slate-500 mt-1">
          Český výrobce kol (Čelákovice), staví na zakázku ~10 dní. Nahoře <strong>4 karty kol</strong> pro KTM
          (varianty = přední / zadní / sada × ráfek, ceny z jejich konfigurátoru, barvy jako volby na kartě),
          dole <strong>díly</strong> (náby, ráfky, adaptéry, paprsky…). Cena = cena dodavatele 1 : 1, dealerská sleva
          je 10 % ⇒ mimo zákaznické slevy.
        </p>
      </header>

      {stats && (
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          <span>Kola na eshopu: <strong>{stats.kola.naEshopu}</strong> / {stats.kola.celkem}</span>
          <span>Díly na eshopu: <strong>{stats.dily.naEshopu}</strong> / {stats.dily.celkem}</span>
          <span>Čeká na robota: <strong>{stats.kola.ceka + stats.dily.ceka}</strong></span>
          <span>Vedeme v ABŘE: <strong>{stats.dily.vedeme}</strong></span>
          {stats.posledniBeh && (
            <span className="text-slate-400">
              sběr {new Date(stats.posledniBeh.kdy).toLocaleString("cs-CZ")} · {stats.posledniBeh.dilu} dílů · {stats.posledniBeh.kolaBodu} cen kol
              {stats.posledniBeh.zmenaCeny > 0 && ` · ${stats.posledniBeh.zmenaCeny}× změna ceny`}
              {!stats.posledniBeh.anonymniOk && <strong className="text-red-700"> · ⛔ kontrola anonymity neprošla</strong>}
            </span>
          )}
        </div>
      )}

      <ZmenyCen />

      <section>
        <h2 className="font-medium mb-2">Kola pro KTM (od 2017)</h2>
        {kola === null ? <LoadingBlock /> : kola.length === 0 ? (
          <EmptyState title="Karty kol ještě nejsou" description="Ještě neproběhl sběr z konfigurátoru (pnpm angelswheels:import)." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* karta = CO SE KUPUJE (přední 21 · zadní 18 · zadní 19 · sada) × náboj; varianta = ráfek */}
            {kola.map((k) => <KartaKola key={k.key} k={k} onZmena={() => void nactiKola()} />)}
          </div>
        )}
        <p className="mt-2 text-[11px] text-zinc-400">
          🔑 Karta = co se kupuje × náboj, varianta = jen ráfek. G2 ProSeries má zadní kolo jen 18×2,15 (19&quot; a sada 21+19 jsou jen Kite). Kite nemá žlutý ráfek.
          Cenový bod, který konfigurátor tiše přepnul na jiný náboj, se na eshop neposílá.
        </p>
      </section>

      <section>
        <h2 className="font-medium mb-2">Díly</h2>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {stats?.police.map((p) => (
            <Button key={p.key} size="sm" variant={police === p.key ? "primary" : "ghost"} onClick={() => setPolice(police === p.key ? null : p.key)}>
              {p.nazev}<span className="ml-1 opacity-60">{p.pocet}</span>
            </Button>
          ))}
          <span className="mx-2 h-5 w-px bg-slate-200" />
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={jenVedeme} onChange={(e) => setJenVedeme(e.target.checked)} />
            jen co vedeme v ABŘE
          </label>
          <input value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="název, kód…" className="ml-auto w-64 rounded border border-slate-300 px-3 py-1.5 text-sm" />
        </div>
        {loading && !dily ? <LoadingBlock /> : null}
        {dily && dily.length === 0 ? <EmptyState title="Nic tu není" description="Zkus jiný filtr, nebo ještě neproběhl sběr (pnpm angelswheels:import)." /> : null}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {dily?.map((p) => <KartaDilu key={p.ref} p={p} onZmena={() => void nactiDily()} />)}
        </div>
      </section>
    </div>
  );
}
